import React, { useState, useEffect } from 'react';
import Header from '../components/landing/Header';
import TopBar from '../components/landing/TopBar';
import Footer from '../components/landing/Footer';
import EnquiryModal from '../components/landing/EnquiryModal';
import { Lightbulb, Rocket, Zap, Award, CheckCircle, Target, Users } from 'lucide-react';
import api from '../utils/axios';
import getFullUrl from '../utils/formatUrl';

const Innovations = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isInquiryOpen, setIsInquiryOpen] = useState(false);
    const [content, setContent] = useState({});

    useEffect(() => {
        window.scrollTo(0, 0);
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        fetchContent();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const fetchContent = async () => {
        try {
            const res = await api.get('/content');
            const contentMap = {};
            res.data.data.forEach(item => {
                contentMap[item.key] = item.value;
            });
            setContent(contentMap);
        } catch (err) {
            console.error("Failed to load web content", err);
        }
    };

    const stats = [
        { label: 'Startups Incubated', value: content.innovations_startups || '20+', icon: Rocket },
        { label: 'Tech Prototypes', value: content.innovations_prototypes || '120+', icon: Zap },
        { label: 'Industry Partners', value: content.innovations_partners || '80+', icon: Users },
        { label: 'Awards Won', value: content.innovations_awards || '15+', icon: Award }
    ];

    return (
        <div className="min-h-screen bg-white font-sans text-slate-900">
            {!isScrolled && <TopBar data={content} />}
            <Header 
                isScrolled={isScrolled} 
                onApply={() => setIsInquiryOpen(true)}
                logo={content.site_logo}
                email={content.contact_email}
                phone={content.contact_phone}
                applyUrl={content.apply_url}
                colleges={content.colleges_list}
                placements={content.placements_list}
                research={content.research_list}
                journals={content.journals_list}
                innovations={content.innovations_list}
            />

            {/* Internal Hero */}
            <header className="relative h-[450px] bg-slate-900 flex items-center overflow-hidden">
                <div className="absolute inset-0 bg-[#003366]/85 z-10"></div>
                <img 
                    src={getFullUrl(content.innovations_hero_image) || "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?q=80&w=2000&auto=format&fit=crop"} 
                    className="absolute inset-0 w-full h-full object-cover"
                    alt="Innovations"
                />
                <div className="max-w-7xl mx-auto px-4 relative z-20 w-full text-center">
                    <h4 className="text-amber-400 font-black uppercase tracking-[8px] text-xs mb-4 text-glow">Future Redefined</h4>
                    <h1 className="text-5xl md:text-7xl font-black text-white uppercase tracking-tighter">
                        Innovations <br /><span className="text-[#cc0000]">Hub</span>
                    </h1>
                    <div className="h-2 w-24 bg-white mx-auto mt-8"></div>
                </div>
            </header>

            {/* Stats Grid */}
            <section className="relative z-30 -mt-16 max-w-7xl mx-auto px-4">
                <div className="bg-white shadow-2xl rounded-sm grid grid-cols-2 lg:grid-cols-4 border-b-8 border-amber-400">
                    {stats.map((s, i) => (
                        <div key={i} className="p-10 text-center border-r last:border-0 border-slate-100 group">
                            <s.icon size={40} className="mx-auto text-[#003366] mb-6 group-hover:scale-110 transition-transform" />
                            <div className="text-4xl font-black text-slate-900 tracking-tighter mb-2">{s.value}</div>
                            <div className="text-[10px] font-black text-slate-500 uppercase tracking-[3px]">{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Introduction */}
            <section className="py-32 bg-slate-50">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex flex-col lg:flex-row gap-24 items-center">
                        <div className="lg:w-1/2 relative order-2 lg:order-1">
                            <div className="absolute -top-10 -left-10 w-40 h-40 bg-amber-400/10 rounded-full blur-3xl"></div>
                            <img 
                                src={getFullUrl(content.innovations_intro_image) || "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=1000&auto=format&fit=crop"} 
                                className="rounded-2xl shadow-3xl relative z-10 border-8 border-white" 
                                alt="Innovation Lab" 
                            />
                        </div>
                        <div className="lg:w-1/2 space-y-8 order-1 lg:order-2">
                            <h3 className="text-[#cc0000] font-black uppercase tracking-[5px] text-[11px]">Entrepreneurship & Tech</h3>
                            <h2 className="text-5xl font-black uppercase text-slate-900 tracking-tight leading-none">
                                {content.innovations_intro_title || 'Incubating the Future Giants'}
                            </h2>
                            <p className="text-slate-600 leading-relaxed text-lg font-medium">
                                {content.innovations_intro_desc || 'The Innovations Hub at Arya Sabhyata is a state-of-the-art startup ecosystem. We provide budding entrepreneurs with mentorship, seed funding, and technical resources to transform their ideas into market-ready products. Our mission is to foster a culture of problem-solving and creative excellence.'}
                            </p>
                            <div className="space-y-4 pt-4">
                                {[
                                    'Equity-free Seed Funding Support',
                                    'Mentorship from Industry Veterans',
                                    'Access to Advanced Prototyping Lab',
                                    'Legal & Patent Filing Assistance'
                                ].map((item, idx) => (
                                    <div key={idx} className="flex items-center space-x-3">
                                        <div className="w-6 h-6 bg-[#003366] text-white rounded-full flex items-center justify-center">
                                            <CheckCircle size={14} />
                                        </div>
                                        <span className="font-bold text-sm uppercase tracking-wide text-slate-700">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Strategic Verticals */}
            <section className="py-24 bg-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center max-w-3xl mx-auto mb-20">
                        <Target className="mx-auto text-[#cc0000] mb-4" size={48} />
                        <h2 className="text-4xl font-black uppercase tracking-tighter">Strategic Impact Verticals</h2>
                        <div className="h-1 w-20 bg-amber-400 mx-auto mt-4"></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { title: 'IoT & Robotics', desc: 'Developing automated solutions for industry 4.0 and smart cities.', icon: Zap },
                            { title: 'FinTech Hub', desc: 'Exploring blockchain, secure payments, and digital banking innovations.', icon: Lightbulb },
                            { title: 'EdTech Lab', desc: 'Reimagining the future of learning through immersive technologies.', icon: Target }
                        ].map((item, idx) => (
                            <div key={idx} className="p-12 bg-white border border-slate-100 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 group">
                                <item.icon size={48} className="text-[#003366] mb-8 group-hover:text-[#cc0000] transition-colors" />
                                <h4 className="text-2xl font-black uppercase mb-4 tracking-tighter">{item.title}</h4>
                                <p className="text-slate-500 text-sm font-medium leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <Footer data={content} />

            <EnquiryModal isOpen={isInquiryOpen} onClose={() => setIsInquiryOpen(false)} />
        </div>
    );
};

export default Innovations;
